import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../../api/axios';
import endpoints from '../../../api/endpoints';
import Loader from '../../../components/common/Loader';
import type { TestDataChartsData, TestDataChartsResponse } from '../../../types/testDataCharts.types';
import TestDataLineChart from './TestDataLineChart';
import TestDataBarChart from './TestDataBarChart';
import TestDataTable from './TestDataTable';
import TocSummaryTable from './TocSummaryTable';
import OutsideVarTable from './OutsideVarTable';

const TestDataCharts: React.FC = () => {
  const [searchParams] = useSearchParams();

  const perfId = searchParams.get('perfId') ?? '';
  const pipName = decodeURIComponent(searchParams.get('pipName') ?? '');
  const numeratorName = decodeURIComponent(searchParams.get('numeratorName') ?? '');
  const denominatorName = decodeURIComponent(searchParams.get('denominatorName') ?? '');
  const valueName = decodeURIComponent(searchParams.get('valueName') ?? '');
  const outsideVarName = decodeURIComponent(searchParams.get('outsideVarName') ?? '');

  const [chartData, setChartData] = useState<TestDataChartsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!perfId) {
      setError('Missing project ID. Please close this tab and try again.');
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    api
      .get<TestDataChartsResponse>(endpoints.ubt.teamsReporting.teamsReportingList, {
        params: { perfId },
        signal: controller.signal,
      })
      .then((response) => {
        setChartData(response.data.data);
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === 'CanceledError') return;
        setError('Failed to load test data. Please close this tab and try again.');
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [perfId]);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="p-8" role="alert" data-testid="error-message">
        <div className="bg-red-50 border border-red-300 rounded p-4 text-red-700 text-sm">
          {error}
        </div>
      </div>
    );
  }

  if (!chartData) return null;

  const isEmpty =
    chartData.tdsList.length === 0 &&
    chartData.tocList.length === 0 &&
    chartData.tdsOutVarList.length === 0;

  if (isEmpty) {
    return (
      <div className="p-8" data-testid="no-data-warning">
        <div className="bg-yellow-50 border border-yellow-300 rounded p-4 text-yellow-800 text-sm">
          The Project <strong>{pipName}</strong> does not have any test data
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="test-data-charts-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-800 mb-1">Test Data Charts</h1>
        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm">
          <div className="grid grid-cols-2 gap-2 max-w-md">
            <span className="font-semibold text-gray-700">Name Of Project:</span>
            <span className="text-gray-900">{pipName}</span>
          </div>
        </div>
      </div>

      {/* Test of Change Summary */}
      {chartData.tocList.length > 0 && (
        <section className="mb-8" data-testid="toc-summary-section">
          <h2 className="text-base font-semibold text-gray-700 mb-2">Test Of Change Summary</h2>
          <TocSummaryTable tocList={chartData.tocList} />
        </section>
      )}

      {/* Line Chart */}
      {chartData.tdsList.length > 0 && (
        <section className="mb-8" data-testid="line-chart-section">
          <h2 className="text-base font-semibold text-gray-700 mb-2">
            {valueName || 'Value'} — Test Data Series
          </h2>
          <div className="bg-white border border-gray-200 rounded p-4">
            <TestDataLineChart tdsList={chartData.tdsList} valueName={valueName || 'Value'} />
          </div>
        </section>
      )}

      {/* Test Data Table */}
      {chartData.tdsList.length > 0 && (
        <section className="mb-8" data-testid="test-data-table-section">
          <h2 className="text-base font-semibold text-gray-700 mb-2">Test Data</h2>
          <TestDataTable
            tdsList={chartData.tdsList}
            numeratorName={numeratorName}
            denominatorName={denominatorName}
            valueName={valueName}
            outsideVarName={outsideVarName}
          />
        </section>
      )}

      {/* Bar Chart */}
      {chartData.tdsOutVarList.length > 0 && (
        <section className="mb-8" data-testid="bar-chart-section">
          <div className="bg-white border border-gray-200 rounded p-4">
            <TestDataBarChart
              tdsOutVarList={chartData.tdsOutVarList}
              valueName={valueName || 'Value'}
              outsideVarName={outsideVarName || 'Other'}
            />
          </div>
        </section>
      )}

      {/* Outside Variable Summary Table */}
      {chartData.tdsOutVarList.length > 0 && (
        <section className="mb-8" data-testid="outside-var-table-section">
          <h2 className="text-base font-semibold text-gray-700 mb-2">
            {valueName || 'Value'} by {outsideVarName || 'Other'}
          </h2>
          <OutsideVarTable
            tdsOutVarList={chartData.tdsOutVarList}
            numeratorName={numeratorName}
            denominatorName={denominatorName}
            outsideVarName={outsideVarName}
          />
        </section>
      )}
    </div>
  );
};

export default TestDataCharts;
